/**
 * Screen shake — rotational jitter applied additively to the camera rig.
 *
 * Driven by sim events (kick connect, crash). Uses a cheap multi-sine noise
 * (no per-frame allocation) and exponential decay. Applied AFTER the rig's
 * own strafe-roll update so it never fights the roll — we add small extra
 * rotation deltas on top of whatever the rig set.
 *
 * There is exactly ONE shake shape: a decaying impulse. Both the kick and the
 * crash are impacts, so both fire on their event frame and decay to nothing
 * over their own duration.
 *
 * The crash used to ALSO drive a sustained "tumble" oscillation for the whole
 * 2.4 s 'crashed' phase, which kept the camera swinging while the ragdoll fell
 * and then cut out abruptly the moment the body settled. That is gone: the
 * crash shake now hits on impact and eases to a dead-still camera over
 * JUICE.shake.crashDuration (0.5 s), with a quadratic decay curve
 * (1 - t/d)^crashDecayExp so the tail is smooth rather than a linear ramp that
 * still has visible motion at its end.
 */

import { JUICE, noise1D, clamp01 } from "./juice";

const SH = JUICE.shake;

/** Active shake impulse (transient, decaying). */
interface ShakeImpulse {
  elapsed: number;
  duration: number;
  rotMag: number; // peak rotational magnitude (deg)
  pitchMag: number; // additional pitch magnitude (deg)
  /** Decay exponent: amplitude = (1 - elapsed/duration)^decayExp. */
  decayExp: number;
}

/**
 * Amplitude envelope of a shake impulse: 1 at the impact frame, 0 at (and
 * after) `duration`. Pure — exported for tests.
 *
 * @param elapsed  seconds since the impulse fired.
 * @param duration total impulse length in seconds (<= 0 → no shake at all).
 * @param decayExp curve exponent (1 = linear ramp-down, 2 = quadratic ease).
 */
export function shakeEnvelope(
  elapsed: number,
  duration: number,
  decayExp: number,
): number {
  if (duration <= 0) return 0;
  const p = clamp01(elapsed / duration);
  const decay = 1 - p;
  return decayExp === 1 ? decay : Math.pow(decay, decayExp);
}

/**
 * Screen-shake controller. Call `triggerKick` / `triggerCrash` from event
 * handlers; call `update(dt)` each render frame and `apply(camera)` to add
 * the shake deltas to the camera rotation.
 */
export class ScreenShake {
  private impulse: ShakeImpulse | null = null;
  private time = 0;

  triggerKick(): void {
    this.impulse = {
      elapsed: 0,
      duration: SH.kickDuration,
      rotMag: SH.kickRotDeg,
      pitchMag: SH.kickRotDeg * 0.4,
      decayExp: 1,
    };
  }

  /**
   * Crash impact shake: fires on the crash frame, decays smoothly to zero over
   * SH.crashDuration and then stops HARD. Nothing shakes while the ragdoll
   * tumbles and settles afterwards.
   */
  triggerCrash(): void {
    this.impulse = {
      elapsed: 0,
      duration: SH.crashDuration,
      rotMag: SH.crashRotDeg,
      pitchMag: SH.crashPitchDeg,
      decayExp: SH.crashDecayExp,
    };
  }

  /** Clear any active shake (called on reset / restart / phase change). */
  reset(): void {
    this.impulse = null;
  }

  /** Advance shake state by `dt` render seconds. */
  update(dt: number): void {
    this.time += dt;
    if (this.impulse) {
      this.impulse.elapsed += dt;
      if (this.impulse.elapsed >= this.impulse.duration) {
        this.impulse = null;
      }
    }
  }

  /**
   * Apply shake deltas to a camera object. Adds to existing rotation.z
   * (roll) and rotation.x (pitch) — call AFTER the rig has set its own
   * roll/pitch so this is purely additive.
   *
   * @param cam  object with `rotation` (Euler) — the rig's PerspectiveCamera.
   */
  apply(cam: { rotation: { z: number; x: number; y: number } }): void {
    if (!this.impulse) return;
    const t = this.time;
    const nf = SH.noiseFreq;
    const decay = shakeEnvelope(
      this.impulse.elapsed,
      this.impulse.duration,
      this.impulse.decayExp,
    );
    if (decay <= 0) return;
    const n1 = noise1D(t * nf, 1.3);
    const n2 = noise1D(t * nf + 100, 4.7);
    const rollDeg = n1 * this.impulse.rotMag * decay;
    const pitchDeg = n2 * this.impulse.pitchMag * decay;
    cam.rotation.z += rollDeg * (Math.PI / 180);
    cam.rotation.x += pitchDeg * (Math.PI / 180);
  }

  /** Whether any shake is currently active (for skipping work when idle). */
  get active(): boolean {
    return this.impulse !== null;
  }
}
